import DocumentViewerModal from "components/DocumentViewer";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { addAppealNormalFeedback } from "services/appealService";
import {
  AppealNormalFeedback,
  AppealNormalFeedbackActor,
  FILE_URL,
  Roles,
} from "types";
import { feedbackIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  type?: "defendant" | "prosecutor";
  appealId: number;
  normal_feedbacks: AppealNormalFeedback[];
  fetchData: () => void;
};

const AppealFeedbackSection: React.FC<Props> = ({
  type = "prosecutor",
  appealId,
  normal_feedbacks,
  fetchData,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const [newComment, setNewComment] = React.useState("");
  const [feedbackFile, setFeedbackFile] = React.useState<File | null>(null);

  return (
    <div className="ml-6">
      <h1 className="text-black font-semibold text-base flex flex-row gap-2">
        {feedbackIcon} {t("feedback_section")}
      </h1>
      <div className="flex flex-col gap-4 mt-4 justify-between h-[50vh] w-full">
        <div className="flex flex-col overflow-y-auto gap-2">
          <div className="flex flex-col gap-4 mt-4 justify-between h-[50vh] w-full">
            {normal_feedbacks?.length === 0 && (
              <p className="text-gray-500 text-sm">
                {t("no_feedbacks_available")}
              </p>
            )}
            <div className="flex flex-col gap-0 w-full">
              {normal_feedbacks?.map((feedback, index) => (
                <div
                  key={index}
                  className={`${
                    index % 2 === 0 ? "bg-white" : "bg-gray-100"
                  } px-6 py-4 rounded-md shadow-sm mb-4`}
                  style={{
                    overflowWrap: "break-word",
                  }}
                >
                  <div className="flex justify-between items-center">
                    <p className="text-xs font-medium text-red-600">
                      {t("commented_date")}
                    </p>
                    <p className="text-xs text-gray-700">
                      {feedback?.created_at &&
                        new Date(feedback?.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-600 text-xs font-bold">
                      {feedback?.actor === "PROSECUTOR" && "P"}
                      {feedback?.actor === "REGISTRAR" && "R"}
                      {feedback?.actor === "COORDINATOR" && "C"}
                      {feedback?.actor === "OTHER" && "O"}
                    </span>
                    <span className="text-xs text-gray-700">
                      <span className="font-semibold text-gray-900">
                        {t("by")}
                      </span>{" "}
                      {feedback?.actor &&
                        AppealNormalFeedbackActor[feedback?.actor]}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-col gap-2">
                    <p className="text-sm text-gray-800 whitespace-pre-line">
                      {feedback?.feedback_text}
                    </p>

                    <DocumentViewerModal
                      documents={(feedback?.documents || []) as any}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.APPEAL}`}
                      layout="list"
                      hideEmptyFile={true}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                boxShadow: "rgb(217 217 217 / 90%) 0px -6px 7px 0px",
              }}
              className="px-6 py-4 w-full bg-white rounded-lg mt-4"
            >
              <p className="mb-2 font-semibold text-gray-800">
                {isCoordinator ? t("write_feedback") : t("write_response_back")}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                <textarea
                  rows={3}
                  className="flex-1 bg-[#F2F2F2] px-3 py-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#212121] text-sm"
                  placeholder={t("write_your_feedback")}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
              </div>
              <div className="mt-3 flex flex-row gap-2 justify-end">
                <label className="flex items-center cursor-pointer text-xs text-gray-700 bg-gray-100 px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-200 transition w-full justify-between">
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setFeedbackFile(e.target.files[0]);
                      }
                    }}
                  />
                  <span className="truncate max-w-[80px]">
                    {feedbackFile ? feedbackFile.name : t("attach_file")}
                  </span>
                  {feedbackFile && (
                    <button
                      type="button"
                      className="ml-2 text-gray-400 hover:text-red-500"
                      onClick={() => setFeedbackFile(null)}
                      title={t("remove_file")}
                    >
                      &times;
                    </button>
                  )}
                </label>
                <button
                  className="bg-[#212121] text-white text-sm px-6 py-2 rounded-md font-semibold hover:bg-gray-800 transition min-w-[100px]"
                  onClick={() => {
                    if (!newComment.trim()) {
                      showTooltip(t("please_enter_comment"), "error");
                      return;
                    }

                    addAppealNormalFeedback({
                      actor: isProsecutor
                        ? "PROSECUTOR"
                        : isRegistrar
                          ? "REGISTRAR"
                          : isCoordinator
                            ? "COORDINATOR"
                            : "OTHER",
                      feedback_text: newComment.trim(),
                      feedback_file: feedbackFile,
                      appeal_request:
                        type === "defendant" ? undefined : appealId,
                      defendant_appeal_request:
                        type === "defendant" ? appealId : undefined,
                    })
                      .then(() => {
                        setNewComment("");
                        setFeedbackFile(null);
                        fetchData();
                        showTooltip(t("comment_added_successfully"), "success");
                      })
                      .catch((error) => {
                        showTooltip(
                          errorHandlerOnForm(error) ||
                            t("error_adding_comment"),
                          "error",
                        );
                      });
                  }}
                >
                  {t("send")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppealFeedbackSection;
